import { nextTick, reactive } from 'vue'
import { isObject } from 'lodash-es'
import { buildFilterPayload, resolveQueryFiltersParam } from '../filter/filter-utils'
import {
  parsePageResult,
  resolveHeadIdField,
  resolveHeadIdValue,
  resolveModuleUrls,
  type BaseControllerPageResult,
} from '../api/api-convention'
import { formatQueryError } from '../api/format-query-error'
import {
  applyQueryPaginationOverride,
  mergeGridQueryParams,
} from '../utils/append-extra-query-params'
import {
  normalizeGlobalSearchKeyword,
  resolveSearchFieldsParam,
} from '../utils/global-search'
import { isInfiniteScrollEnabled } from '../utils/infinite-scroll'
import { resolveGridRequest } from '../utils/resolve-grid-request'
import { resolveGridContainerWidth } from '../utils/resolve-layout-width'
import type { DataGrid, FilterPayloadNode, GridOptions, GridQueryParams } from '../types'

export function getGridOptions($grid: DataGrid): GridOptions {
  return $grid.options
}

export async function reloadGridData($grid: DataGrid) {
  await $grid.queryByExamplePage()
}

export function useQuery($grid: DataGrid) {
  let querySeq = 0

  const queryByExamplePage = async (
    overrideParams?: GridQueryParams,
    queryOptions?: { append?: boolean }
  ) => {
    const append = queryOptions?.append === true
    const seq = ++querySeq
    const { reactData } = $grid
    const options = getGridOptions($grid)
    const urls = resolveModuleUrls(options.module)
    const url = options.queryUrl || urls.queryByExamplePage
    const { actived } = reactData.editStore

    const params: Record<string, unknown> = {
      currentPage: reactData.currentPage,
      pageSize: reactData.pageSize,
      pageFlag: false,
    }
    mergeGridQueryParams(params, options.params, overrideParams)
    applyQueryPaginationOverride(reactData, params, overrideParams)

    // 无限滚动下非追加查询一律从第 1 页重载，避免刷新停留在中间页
    if (!append && isInfiniteScrollEnabled(options) && overrideParams?.currentPage == null) {
      reactData.currentPage = 1
      params.currentPage = 1
    }

    const rawKeyword =
      typeof overrideParams?.keyword === 'string'
        ? overrideParams.keyword
        : reactData.searchKeyword
    const { keyword } = normalizeGlobalSearchKeyword(rawKeyword)
    if (keyword) {
      params.keyword = keyword
      const searchFields = resolveSearchFieldsParam(
        options.searchFields,
        options.columns
      )
      if (searchFields) {
        params.searchFields = searchFields
      } else {
        delete params.searchFields
      }
    } else {
      delete params.keyword
      delete params.searchFields
    }

    if (append) {
      reactData.loadingMore = true
    } else {
      reactData.loading = true
    }
    reactData.loadError = false

    const userFilters = $grid.getFilters?.() ?? buildFilterPayload(reactData.filterGroups)
    const sorts = $grid.getSorts()

    const systemFilters: FilterPayloadNode[] = []
    if (isObject(options.parent)) {
      const headId = resolveHeadIdField(options)
      if (headId) {
        systemFilters.push({
          field: headId,
          operator: '=',
          logic: 'and',
          value: resolveHeadIdValue(options),
        })
      }
    }

    if (!Array.isArray(params.filters)) {
      params.filters = []
    }

    options.beforeQuery?.(params as GridQueryParams)

    if (Array.isArray(params.filters) && params.filters.length > 0) {
      systemFilters.push(...params.filters)
    }

    const filtersParam = resolveQueryFiltersParam(userFilters, systemFilters)
    if (filtersParam) {
      params.filters = filtersParam
    } else {
      delete params.filters
    }
    if (sorts.length > 0) {
      params.sorts = JSON.stringify(sorts)
    }

    try {
      const request = resolveGridRequest($grid)
      const res = await request.get<BaseControllerPageResult<Record<string, unknown>[]>>({ url, params })
      if (seq !== querySeq) {
        return
      }
      const { list, total } = parsePageResult<Record<string, unknown>>(res)
      reactData.total = total

      if (!append) {
        reactData.editStr = ''
        actived.row = null
        actived.field = null
      }

      try {
        await options.afterQueryPage?.(list)
      } catch {
        // afterQueryPage 失败（如辅助接口 404）不应阻断主列表渲染
      }

      if (append) {
        reactData.tableData.push(...list)
        $grid.syncCheckboxStatus?.()
        $grid.updateFooter?.()
        await nextTick()
        $grid.recalculateColumnWidths?.(resolveGridContainerWidth($grid))
      } else {
        const prevCurrentId = reactData.currentRow?.['id']
        reactData.tableData = reactive(list)
        const targetRow: Record<string, unknown> | null =
          prevCurrentId != null
            ? list.find((item) => item['id'] === prevCurrentId) ?? list[0] ?? null
            : list[0] ?? null

        $grid.syncCheckboxStatus?.()
        $grid.updateFooter?.()

        await nextTick()
        if (isInfiniteScrollEnabled(options)) {
          await $grid.scrollToTop?.()
        }
        $grid.setCurrentRow(targetRow, { silent: true })
        $grid.recalculateColumnWidths?.(resolveGridContainerWidth($grid))
      }
    } catch (e: unknown) {
      if (seq !== querySeq) {
        return
      }
      if (append) {
        // 追加失败回退页码，避免跳页空洞
        reactData.currentPage = Math.max(1, reactData.currentPage - 1)
      } else {
        reactData.loadError = true
        reactData.loadErrorMsg = formatQueryError(e)
      }
      throw e
    } finally {
      if (seq === querySeq) {
        reactData.loading = false
        reactData.loadingMore = false
      }
    }
  }

  const loadMore = async () => {
    const { reactData } = $grid
    const options = getGridOptions($grid)
    if (!isInfiniteScrollEnabled(options)) {
      return
    }
    if (
      reactData.loading
      || reactData.loadingMore
      || reactData.loadError
      || reactData.tableData.length >= reactData.total
    ) {
      return
    }
    reactData.currentPage += 1
    await queryByExamplePage(undefined, { append: true })
  }

  $grid.queryByExamplePage = queryByExamplePage
  $grid.loadMore = loadMore
  getGridOptions($grid).queryByExamplePage = queryByExamplePage

  $grid.loadTableData = async (datas: unknown[]) => {
    const { reactData } = $grid
    const options = getGridOptions($grid)
    const { actived } = reactData.editStore
    const list = (Array.isArray(datas) ? datas : []) as Record<string, unknown>[]

    reactData.editStr = ''
    actived.row = null
    actived.field = null
    reactData.loadError = false
    reactData.loadErrorMsg = ''
    reactData.total = list.length
    reactData.tableData = reactive(list)
    const targetRow = list[0] ?? null

    $grid.syncCheckboxStatus?.()
    $grid.updateFooter?.()

    await nextTick()
    $grid.setCurrentRow(targetRow, { silent: true })
    $grid.recalculateColumnWidths?.(resolveGridContainerWidth($grid))
  }
}
